import { ArrowRight, Loader2, Plus, Upload, Trash2 } from "lucide-react";
import type { ChangeEvent } from "react";
import { useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { readSheet } from "read-excel-file/browser";
import type { FixedCost } from "../dashboard/company/types";
import {
  previewFinanceFixedCostsImport,
  saveFinanceFixedCosts,
} from "../../services/financeService";
import { FinanceOnboardingCta } from "./FinanceOnboardingCta";

type StepProps = {
  onNext: () => void;
};

const currencies = [
  { code: "COP", label: "🇨🇴 COP" },
  { code: "USD", label: "🇺🇸 USD" },
  { code: "EUR", label: "🇪🇺 EUR" },
  { code: "MXN", label: "🇲🇽 MXN" },
];

const formatCurrency = (value: number, currency: string) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value || 0);

const createCost = (label = "", amount = 0): FixedCost => ({
  id: `cost-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  label,
  amount,
});

export const Step4FixedCosts = ({ onNext }: StepProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [costs, setCosts] = useState<FixedCost[]>([createCost()]);
  const [currency, setCurrency] = useState("COP");
  const [importSummary, setImportSummary] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  const total = useMemo(
    () => costs.reduce((acc, cost) => acc + Number(cost.amount || 0), 0),
    [costs]
  );

  const updateCost = (id: string, patch: Partial<FixedCost>) => {
    setImportSummary(null);
    setCosts((current) => current.map((cost) => (cost.id === id ? { ...cost, ...patch } : cost)));
  };

  const addCost = (label = "") => {
    setImportSummary(null);
    setCosts((current) => [...current, createCost(label)]);
  };

  const removeCost = (id: string) => {
    setImportSummary(null);
    setCosts((current) => (current.length === 1 ? [createCost()] : current.filter((cost) => cost.id !== id)));
  };

  const persistAndContinue = async () => {
    setSaving(true);
    try {
      const cleanCosts = costs
        .map((cost) => ({ ...cost, label: cost.label.trim(), amount: Number(cost.amount || 0), currency }))
        .filter((cost) => cost.label);
      await saveFinanceFixedCosts(cleanCosts, currency);
      onNext();
    } catch {
      toast.error("No pudimos guardar los costos fijos.");
    } finally {
      setSaving(false);
    }
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setImporting(true);
    try {
      const table = await extractTableText(file);
      const preview = await previewFinanceFixedCostsImport(file.name, table.text, currency, table.rowsCount);
      const importedCosts = preview.costs ?? [];
      if (importedCosts.length === 0) {
        toast.error("No encontramos costos fijos claros en el archivo.");
        return;
      }
      setCosts(importedCosts.map((cost) => ({ ...cost, currency })));
      setImportSummary(`Agrupamos ${preview.sourceRowsCount || table.rowsCount} movimientos en ${preview.groupedCostsCount || importedCosts.length} costos mensuales editables.`);
      toast.success("Costos importados");
    } catch {
      toast.error("No pudimos leer ese archivo.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="w-full max-w-4xl rounded-[42px] bg-white p-7 shadow-xl md:p-10">
      <div className="space-y-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-xl">
            <h1 className="text-4xl font-bold text-gray-900">
              Costos fijos <span className="text-fluxi">mensuales</span>
            </h1>
            <p className="mt-4 text-base font-medium leading-7 text-[#64748B]">
              Registra lo que pagas cada mes aunque no vendas: arriendo, nómina, herramientas o plataformas.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-[128px_1fr] lg:min-w-[330px]">
            <label className="block">
              <span className="mb-2 block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-400">
                Moneda
              </span>
              <select
                value={currency}
                onChange={(event) => {
                  setCurrency(event.target.value);
                  setCosts((current) => current.map((cost) => ({ ...cost, currency: event.target.value })));
                }}
                className="h-11 w-full rounded-full border border-gray-200 bg-white px-4 text-sm font-bold text-gray-900 outline-none transition focus:border-fluxi"
              >
                {currencies.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>

            <div>
              <span className="mb-2 block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-400">
                Total mensual
              </span>
              <div className="flex h-11 items-center rounded-full border border-blue-100 bg-blue-50 px-5 text-xl font-bold text-gray-900">
                {formatCurrency(total, currency)}
              </div>
            </div>
          </div>
        </div>

        {importSummary ? (
          <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
            {importSummary}
          </div>
        ) : null}

        <div className="space-y-2">
          {costs.map((cost) => (
            <div key={cost.id} className="grid gap-2 rounded-[22px] border border-gray-100 bg-gray-50/70 p-2 md:grid-cols-[1fr_190px_40px]">
              <input
                value={cost.label}
                onChange={(event) => updateCost(cost.id, { label: event.target.value })}
                placeholder="Concepto"
                className="h-11 rounded-full border border-gray-200 bg-white px-4 text-sm font-medium text-gray-900 outline-none transition focus:border-fluxi"
              />
              <input
                value={cost.amount || ""}
                onChange={(event) =>
                  updateCost(cost.id, { amount: Number(event.target.value.replace(/[^\d]/g, "")) })
                }
                inputMode="numeric"
                placeholder="Valor mensual"
                className="h-11 rounded-full border border-gray-200 bg-white px-4 text-right text-sm font-semibold text-gray-900 outline-none transition focus:border-fluxi"
              />
              <button
                type="button"
                onClick={() => removeCost(cost.id)}
                className="inline-flex h-11 w-11 items-center justify-center text-gray-400 transition hover:bg-red-50 hover:text-red-500"
                aria-label="Eliminar costo"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-4 border-t border-gray-100 pt-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => addCost()}
              className="inline-flex h-11 items-center justify-center gap-2 border border-gray-200 bg-white px-5 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-gray-300 hover:bg-gray-50"
            >
              <Plus className="h-4 w-4" />
              Agregar costo
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="inline-flex h-11 items-center justify-center gap-2 border border-blue-100 bg-blue-50 px-5 text-sm font-semibold text-fluxi shadow-sm transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Importar Excel
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv,.txt"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={onNext}
              className="inline-flex h-11 items-center justify-center px-5 text-sm font-semibold text-gray-500 transition hover:bg-gray-50 hover:text-gray-900"
            >
              Lo configuro después
            </button>
            <FinanceOnboardingCta onClick={persistAndContinue} disabled={saving || importing}>
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
              Continuar <ArrowRight className="h-5 w-5" />
            </FinanceOnboardingCta>
          </div>
        </div>
      </div>
    </div>
  );
};

async function extractTableText(file: File) {
  if (file.name.toLowerCase().endsWith(".csv") || file.name.toLowerCase().endsWith(".txt")) {
    const text = await file.text();
    return {
      text: text.slice(0, 12000),
      rowsCount: text.split(/\r?\n/).filter((row) => row.trim()).length,
    };
  }

  const rows = await readSheet(file);
  return {
    text: rows
      .slice(0, 120)
      .map((row: unknown[]) => row.map((cell: unknown) => String(cell ?? "")).join(" | "))
      .join("\n")
      .slice(0, 12000),
    rowsCount: rows.filter((row: unknown[]) => row.some((cell: unknown) => String(cell ?? "").trim())).length,
  };
}
